import * as React from 'react';
import { useState, useEffect } from 'react';
import DeleteConfirmationModalComponent from './DeleteConfirmationModalComponent'
import ConfirmationModalComponent from './ConfirmationModalComponent'
import ProblemSearchComponent from './ProblemSearchComponent';
import { EditorState, convertToRaw, ContentState } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';
import ProblemBuilder from './ProblemBuilder';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons/faAngleDown'
import { faAngleUp } from '@fortawesome/free-solid-svg-icons/faAngleUp'
import { faMinus } from '@fortawesome/free-solid-svg-icons/faMinus'
import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus'
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons/faPenToSquare'
import { faSquarePlus } from '@fortawesome/free-solid-svg-icons/faSquarePlus'
import { faSquareMinus } from '@fortawesome/free-solid-svg-icons/faSquareMinus'
import { faVideo } from '@fortawesome/free-solid-svg-icons/faVideo'
import { faHeadphones } from '@fortawesome/free-solid-svg-icons/faHeadphones'
import { faGlobe } from '@fortawesome/free-solid-svg-icons/faGlobe'
import { faCopy } from '@fortawesome/free-solid-svg-icons/faCopy'
import { CPProblem } from '../models/CPProblem';
import { CPAssignment } from '../models/CPAssignment';
import ThreeOptionModalComponent from './ThreeOptionModalComponent';
import WarningModalComponent from './WarningModalComponent';
import { CPReactMethods } from '../app';
import TwoOptionModalComponent from './TwoOptionModalComponent';
import PopupModalComponent from './PopupModalComponent';
import ImageDisplayComponent from './ImageDisplayComponent';
import BreadcrumbComponent from './BreadcrumbComponent';
import RichTextEditorComponent from './RichTextEditorComponent';
import { Collapse } from 'react-bootstrap';
import AssignmentBuilderProblemCard from './AssignmentBuilderProblemCard';
import { forEach } from 'core-js/fn/array';
import { CPPart } from '../models/CPPart';
var deepEqual = require('deep-equal');
const cloneDeep = require('lodash.clonedeep')

export interface AssignmentBuilderProps {
    assignment: CPAssignment
    helpTextHtml: string
    limitedEditing: boolean
    isSharedAssignment: boolean
    problemLimitEditing: Record<number, boolean>
    problemUserIsOwner: Record<number, boolean>
    problemInMultiplePlaces: Record<number, boolean>
    problemAnsweredInAssignment: Record<number, boolean>
    courseGuid?: string
}

const AssignmentBuilder = (props: AssignmentBuilderProps) => {
    const [initialCPAssignment, setInitialCPAssignment] = useState<CPAssignment>(cloneDeep(props.assignment))
    const [assignmentId, setAssignmentId] = useState<number | undefined>(props.assignment.id)
    const [assignmentGuid, setAssignmentGuid] = useState<string | undefined>(props.assignment.guid)
    const [problems, setProblems] = useState<Array<CPProblem>>(props.assignment.problems)
    const [problemUserIsOwner, setProblemUserIsOwner] = useState<Record<number, boolean>>(props.problemUserIsOwner);
    const [problemInMultiplePlaces, setProblemInMultiplePlaces] = useState<Record<number, boolean>>(props.problemInMultiplePlaces);
    const [problemLimitEditing, setProblemLimitEditing] = useState<Record<number, boolean>>(props.problemLimitEditing);
    const [problemAnsweredInAssignment, setProblemAnsweredInAssignment] = useState<Record<number, boolean>>(props.problemAnsweredInAssignment);
    const [ownerUserId, setOwnerUserId] = useState<number | undefined>(props.assignment.ownerUserId)
    const [taskPartConfigurationId, setTaskPartConfigurationId] = useState(props.assignment.taskPartConfigurationId)
    const [taskPartId, setTaskPartId] = useState(props.assignment.taskPartId)
    const [topicTaskEntityId, setTopicTaskEntityId] = useState(props.assignment.topicTaskEntityId)
    const [shareable, setShareable] = useState(props.assignment.shareable)
    const [name, setName] = useState(props.assignment.name)
    const [overview, setOverview] = useState(props.assignment.overview || "")
    var overviewToBlocks = htmlToDraft(overview || "")
    const [editorState, setEditorState] = useState(
        () => EditorState.createWithContent(ContentState.createFromBlockArray(overviewToBlocks.contentBlocks, overviewToBlocks.entityMap)),
    )
    const [status, setStatus] = useState(props.assignment.status)
    const [defaultPointValue, setDefaultPointValue] = useState(props.assignment.defaultPartPointValue)
    const [defaultUnpenalizedAttempts, setDefaultUnpenalizedAttempts] = useState(props.assignment.defaultUnpenalizedAttempts)
    const [defaultMaxAttempts, setDefaultMaxAttempts] = useState(props.assignment.defaultMaxAttempts)
    const [defaultPenalty, setDefaultPenalty] = useState(props.assignment.defaultPenalty)
    const [defaultPlusMinus, setDefaultPlusMinus] = useState(props.assignment.defaultPlusMinus)
    const [defaultPlusMinusIsPercent, setDefaultPlusMinusIsPercent] = useState(props.assignment.defaultPlusMinusIsPercent)
    const [gradedPoints, setGradedPoints] = useState(props.assignment.gradedPoints)
    const [isAddingProblem, setIsAddingProblem] = useState(false)
    const [addProblemIndex, setAddProblemIndex] = useState(0)

    const [selectedProblem, setSelectedProblem] = useState<CPProblem | undefined>(undefined)
    const [showHelp, setShowHelp] = useState(false)
    const [showTags, setShowTags] = useState(false)
    const [showEquations, setShowEquations] = useState(false)
    const [showGuid, setShowGuid] = useState(false)
    const [showAssignmentBuilderConfig, setShowAssignmentBuilderConfig] = useState(true)
    const [showAssignmentBuilderHelp, setShowAssignmentBuilderHelp] = useState(true)

    const [hiddenProblems, setHiddenProblems] = useState<Array<boolean>>(Array(3))
    const [pendingChanges, setPendingChanges] = useState<boolean>(false)
    const [warningContent, setWarningContent] = useState("")
    const [popupContent, setPopupContent] = useState("")
    const [lastTimeSaved, setLastTimeSaved] = useState<string | undefined>()
    const [isAssignmentPreview, setIsAssignmentPreview] = useState(false)
    const [limitedEditing, setLimitedEditing] = useState(props.limitedEditing)
    const [isSharedAssignment, setIsSharedAssignment] = useState(props.isSharedAssignment)
    const [isLoading, setIsLoading] = useState(false)

    const isGlobalAdmin = () => {
        return (initialCPAssignment.ownerUserId == null && shareable == true && taskPartConfigurationId == null)
    }

    useEffect(() => {
        if (initialCPAssignment) {
            if (assignmentId != initialCPAssignment.id ||
                assignmentGuid != initialCPAssignment.guid ||
                name != initialCPAssignment.name ||
                (overview != initialCPAssignment.overview && (overview != "" && initialCPAssignment.overview != null)) ||
                defaultPlusMinus != initialCPAssignment.defaultPlusMinus ||
                defaultPlusMinusIsPercent != initialCPAssignment.defaultPlusMinusIsPercent ||
                defaultPointValue != initialCPAssignment.defaultPartPointValue ||
                defaultMaxAttempts != initialCPAssignment.defaultMaxAttempts ||
                defaultUnpenalizedAttempts != initialCPAssignment.defaultUnpenalizedAttempts ||
                defaultPenalty != initialCPAssignment.defaultPenalty ||
                ownerUserId != initialCPAssignment.ownerUserId ||
                status != initialCPAssignment.status ||
                gradedPoints != initialCPAssignment.gradedPoints ||
                !deepEqual(problems, initialCPAssignment.problems)) {
                setPendingChanges(true)
            }
            else {
                setPendingChanges(false)
            }
        }
    }, [initialCPAssignment, assignmentId, assignmentGuid, name, overview, defaultPlusMinus, defaultPlusMinusIsPercent, defaultPointValue, defaultMaxAttempts, problems.flat(2), defaultUnpenalizedAttempts, defaultPenalty, ownerUserId, status, gradedPoints])

    const validateAssignmentForSave = async (assignment: CPAssignment) => {
        var validAssignment = true
        var errorMessage = ""
        await window.CPInstructor.checkAssignment(assignment, calculateTotalPoints()).then(response => {
            if (!response.valid || !response.successful) {
                validAssignment = false
                errorMessage = response.message
            }
        }).catch((error) => {
            validAssignment = false
            errorMessage = error
        })
        if (!validAssignment) {
            throw errorMessage
        }

        /*if (assignment.overview == undefined) {
            throw "Assignment is missing overview text"
        }
        assignment.problems.forEach((problem, problemIndex) => {
            if (problem.text == undefined) {
                throw "Problem " + (problemIndex + 1) + " needs text in order to save."
            }
            if (problem.name == undefined) {
                throw "Problem " + (problemIndex + 1) + " needs a name in order to save."
            }
            if (problem.parts.length == 0) {
                throw "Problem " + (problemIndex + 1) + " needs at least one part in order to save."
            }
        })*/
    }

    const saveAssignmentToApi = (clone: boolean, delink: boolean) => {
        setIsLoading(true)
        var updatedAssignment: CPAssignment = {
            id: assignmentId,
            guid: assignmentGuid,
            name: name,
            overview: overview,
            defaultPlusMinus: defaultPlusMinus,
            defaultPlusMinusIsPercent: defaultPlusMinusIsPercent,
            ownerUserId: ownerUserId,
            problems: cloneDeep(updateProblemOrders(problems)),
            taskPartId: taskPartId,
            taskPartConfigurationId: taskPartConfigurationId,
            status: status,
            defaultPartPointValue: defaultPointValue,
            defaultMaxAttempts: defaultMaxAttempts,
            defaultUnpenalizedAttempts: defaultUnpenalizedAttempts,
            defaultPenalty: defaultPenalty,
            gradedPoints: gradedPoints,
            topicTaskEntityId: topicTaskEntityId,
            shareable: shareable
        }
        try {
            validateAssignmentForSave(updatedAssignment).then(response => {
                window.CPInstructor.setAssignment(updatedAssignment, clone, delink).then(response => {
                    setInitialCPAssignment(response.data)
                    setAssignmentId(response.data.id)
                    setAssignmentGuid(response.data.guid)
                    setProblems(response.data.problems)
                    setProblemLimitEditing(response.problemLimitEditing)
                    setProblemUserIsOwner(response.problemUserIsOwner)
                    setProblemInMultiplePlaces(response.problemInMultiplePlaces)
                    setProblemAnsweredInAssignment(response.problemAnsweredInAssignment)
                    setOwnerUserId(response.data.ownerUserId)
                    setTaskPartConfigurationId(response.data.taskPartConfigurationId)
                    setTaskPartId(response.data.taskPartId)
                    setTopicTaskEntityId(response.data.topicTaskEntityId)
                    setShareable(response.data.shareable)
                    setName(response.data.name)
                    setOverview(response.data.overview)
                    setStatus(response.data.status)
                    setDefaultPointValue(response.data.defaultPartPointValue)
                    setDefaultUnpenalizedAttempts(response.data.defaultUnpenalizedAttempts)
                    setDefaultMaxAttempts(response.data.defaultMaxAttempts)
                    setDefaultPenalty(response.data.defaultPenalty)
                    setDefaultPlusMinus(response.data.defaultPlusMinus)
                    setDefaultPlusMinusIsPercent(response.data.defaultPlusMinusIsPercent)
                    setGradedPoints(response.data.gradedPoints)
                    setShareable(response.data.shareable)
                    setIsSharedAssignment(response.isSharedAssignment)
                    setLimitedEditing(response.limitedEditing)
                    var overviewToBlocks = htmlToDraft(response.data.overview || "")
                    setEditorState(
                        () => EditorState.createWithContent(ContentState.createFromBlockArray(overviewToBlocks.contentBlocks, overviewToBlocks.entityMap)),
                    )
                    var saveTime = new Date()
                    var hours = saveTime.getHours()
                    hours = (hours == 0 ? 12 : hours > 12 ? hours - 12 : hours)
                    var minutes: string | number = saveTime.getMinutes()
                    minutes = (minutes < 10 ? "0" + minutes : minutes.toString())
                    setLastTimeSaved(hours + ":" + minutes)
                }).catch((error) => {
                    setWarningContent(error.message ?? error)
                    // @ts-ignore
                    var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
                    warningModal.show();
                    setIsLoading(false);
                }).finally(() => {
                    setIsLoading(false)
                })
            }).catch((error) => {
                setWarningContent(error.message ?? error)
                // @ts-ignore
                var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
                warningModal.show();
                setIsLoading(false)
            })
        }
        catch (error) {
            var message: string = "" + error
            setWarningContent(message)
            // @ts-ignore
            var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
            warningModal.show();
            setIsLoading(false);
        }


    }


    const changeName = (input: string) => {
        if (input.length <= 75) {
            setName(input)
        }
    }

    const changeEditor = editorState => {
        const rawContentState = convertToRaw(editorState.getCurrentContent())
        const markup = draftToHtml(
            rawContentState,
        )
        setOverview(markup)
        setEditorState(editorState)
    }

    const moveProblemDown = problemIndex => {
        var updatedProblems: any[] = cloneDeep(problems)
        var problem1 = problems[problemIndex]
        var problem2 = problems[problemIndex + 1]
        updatedProblems[problemIndex + 1] = problem1
        updatedProblems[problemIndex] = problem2
        updatedProblems.forEach((problem, problemIndex) => {
            problem.order = problemIndex + 1
        })
        setProblems(updatedProblems)
    }

    const moveProblemUp = (problemIndex) => {
        var updatedProblems: any[] = cloneDeep(problems)
        var problem1 = updatedProblems[problemIndex - 1]
        var problem2 = updatedProblems[problemIndex]
        updatedProblems[problemIndex] = problem1
        updatedProblems[problemIndex - 1] = problem2
        updatedProblems.forEach((problem, problemIndex) => {
            problem.order = problemIndex + 1
        })
        setProblems(updatedProblems)
    }

    const calculateTotalPoints = () => {
        var totalPoints = 0
        var greatestDecimalPoints = 0
        problems.forEach(problem => {
            var problemPoints = calculateProblemPoints(problem)
            totalPoints += problemPoints
            var decimalPoints = getDecimalPoints(problemPoints)
            if (decimalPoints > greatestDecimalPoints) {
                greatestDecimalPoints = decimalPoints
            }
        })
        return Math.round(totalPoints * Math.pow(10, greatestDecimalPoints)) / Math.pow(10, greatestDecimalPoints)
    }

    const calculateProblemPoints = problem => {
        var totalPoints = 0
        var greatestDecimalPoints = 0;
        problem.parts.forEach(part => {
            var partPoints = getPartPoints(part);
            totalPoints += partPoints
            var decimalPoints = getDecimalPoints(partPoints)
            if (decimalPoints > greatestDecimalPoints) {
                greatestDecimalPoints = decimalPoints
            }
        })
        return Math.round(totalPoints * Math.pow(10, greatestDecimalPoints)) / Math.pow(10, greatestDecimalPoints)
    }

    const getPartPoints = part => {
        if (part.points) {
            return part.points
        }
        else {
            return defaultPointValue
        }
    }

    const getDecimalPoints = (value: number) => {
        var decimalPoints = 0;
        var decimalIndex = value.toString().indexOf('.')
        if (decimalIndex > -1) {
            decimalPoints = value.toString().length - (decimalIndex + 1)
        }
        return decimalPoints;
    }

    const changePointValue = input => {
        if (input.value >= 0 || input.value == "") {
            var updatedPoints: number | undefined = undefined
            while (input.value.length > 1 && input.value[0] == 0) {
                input.value = input.value.substring(1)
            }
            if (input.value != "") {
                updatedPoints = parseFloat(input.value)
            }
            var updatedProblems: Array<CPProblem> = cloneDeep(problems)
            updatedProblems[input.dataset.problemindex].parts[input.dataset.partindex].points = updatedPoints
            setProblems(updatedProblems)
        }
    }

    const changeUnpenalizedAttempts = input => {
        if (input.value >= 0 || input.value == "") {
            var updatedUnpenalizedAttempts: number | undefined = undefined
            while (input.value.length > 1 && input.value[0] == 0) {
                input.value = input.value.substring(1)
            }
            if (input.value != "") {
                updatedUnpenalizedAttempts = parseInt(input.value)
            }
            var updatedProblems: Array<CPProblem> = cloneDeep(problems)
            updatedProblems[input.dataset.problemindex].parts[input.dataset.partindex].unpenalizedAttempts = updatedUnpenalizedAttempts
            setProblems(updatedProblems)
        }
    }

    const changeMaxAttempts = input => {
        if (input.value >= 0 || input.value == "") {
            var updatedMaxAttempts: number | undefined = undefined
            while (input.value.length > 1 && input.value[0] == 0) {
                input.value = input.value.substring(1)
            }
            if (input.value != "") {
                updatedMaxAttempts = parseInt(input.value)
            }
            var updatedProblems: Array<CPProblem> = cloneDeep(problems)
            updatedProblems[input.dataset.problemindex].parts[input.dataset.partindex].maxAttempts = updatedMaxAttempts
            setProblems(updatedProblems)
        }
    }

    const changePenalty = input => {
        if ((input.value >= 0 && input.value <= 100) || input.value == "") {
            var updatedPenalty: number | undefined = undefined
            while (input.value.length > 1 && input.value[0] == 0) {
                input.value = input.value.substring(1)
            }
            if (input.value != "") {
                updatedPenalty = parseFloat(input.value)
            }
            var updatedProblems: Array<CPProblem> = cloneDeep(problems)
            updatedProblems[input.dataset.problemindex].parts[input.dataset.partindex].penalty = updatedPenalty
            setProblems(updatedProblems)
        }
    }

    const changePlusMinus = input => {
        if ((input.value >= 0) || input.value == "") {
            var updatedPlusMinus: number | undefined = undefined
            while (input.value.length > 1 && input.value[0] == 0) {
                input.value = input.value.substring(1)
            }
            if (input.value != "") {
                updatedPlusMinus = parseFloat(input.value)
            }
            var updatedProblems: Array<CPProblem> = cloneDeep(problems)
            updatedProblems[input.dataset.problemindex].parts[input.dataset.partindex].plusMinus = updatedPlusMinus
            setProblems(updatedProblems)
        }
    }

    const changePlusMinusIsPercent = input => {
        var updatedProblems: Array<CPProblem> = cloneDeep(problems)
        var inputBool: boolean | undefined = undefined
        if (input.value == "true") {
            inputBool = true
        }
        if (input.value == "false") {
            inputBool = false
        }
        updatedProblems[input.dataset.problemindex].parts[input.dataset.partindex].plusMinusIsPercent = inputBool
        setProblems(updatedProblems)
    }

    const changeGradedPoints = input => {
        if (input.value >= 0 || input.value == "") {
            while (input.value.length > 1 && input.value[0] == 0) {
                input.value = input.value.substring(1)
            }
            if (input.value == "") {
                input.value = 0
            }
            var updatedGradedPoints = parseInt(input.value)
            setGradedPoints(updatedGradedPoints)
        }
    }

    const changeDefaultPointValue = input => {
        if (input.value >= 0 || input.value == "") {
            while (input.value.length > 1 && input.value[0] == 0) {
                input.value = input.value.substring(1)
            }
            if (input.value == "") {
                input.value = 0
            }
            var updatedDefaultPointValue = parseFloat(input.value)
            setDefaultPointValue(updatedDefaultPointValue)
        }
    }

    const changeDefaultUnpenalizedAttempts = input => {
        if (input.value >= 0 || input.value == "") {
            while (input.value.length > 1 && input.value[0] == 0) {
                if (input.value > 0 && input.value[0] == 0) {
                    input.value = input.value.substring(1)
                }
                else if (input.value = 0) {
                    input.value = "0"
                }
            }
            if (input.value == "") {
                input.value = 0
            }
            var updatedDefaultUnpenalizedAttempts = parseInt(input.value)
            setDefaultUnpenalizedAttempts(updatedDefaultUnpenalizedAttempts)
        }
    }

    const changeDefaultMaxAttempts = input => {
        if (input.value >= 0 || input.value == "") {
            while (input.value.length > 1 && input.value[0] == 0) {
                if (input.value > 0 && input.value[0] == 0) {
                    input.value = input.value.substring(1)
                }
                else if (input.value = 0) {
                    input.value = "0"
                }
            }
            if (input.value == "") {
                input.value = 0
            }
            var updatedDefaultMaxAttempts = parseInt(input.value)
            setDefaultMaxAttempts(updatedDefaultMaxAttempts)
        }
    }

    const changeDefaultPenalty = input => {
        if (input.value >= 0 || input.value == "") {
            while (input.value.length > 1 && input.value[0] == 0) {
                input.value = input.value.substring(1)
            }
            if (input.value == "") {
                input.value = 0
            }
            var updatedDefaultPenalty = parseInt(input.value)
            setDefaultPenalty(updatedDefaultPenalty)
        }
    }

    const changeDefaultPlusMinus = input => {
        if (input.value >= 0 || input.value == "") {
            while (input.value.length > 1 && input.value[0] == 0) {
                input.value = input.value.substring(1)
            }
            if (input.value == "") {
                input.value = 0
            }
            var updatedDefaultPlusMinus = input.value
            setDefaultPlusMinus(updatedDefaultPlusMinus)
        }
    }

    const changeDefaultPlusMinusIsPercent = input => {
        var updatedDefaultPlusMinusIsPercent = input.value
        setDefaultPlusMinusIsPercent(updatedDefaultPlusMinusIsPercent)
    }

    // This one rounds to the second decimal place to always add to the correct total and only slightly change individual values
    const adjustPointTotal = () => {
        var originalPointTotal = calculateTotalPoints()
        var updatedProblems: any[] = cloneDeep(problems)
        var remainingGradedPoints = gradedPoints ?? calculateTotalPoints()
        updatedProblems.forEach((problem, problemIndex) => {
            problem.parts.forEach((part, partIndex) => {
                var unroundedPoints = parseFloat(((part.points ?? defaultPointValue) * remainingGradedPoints / originalPointTotal).toString())
                var roundedPoints = Math.round(unroundedPoints * 100) / 100
                originalPointTotal = originalPointTotal - (part.points ?? defaultPointValue)
                updatedProblems[problemIndex].parts[partIndex].points = roundedPoints
                remainingGradedPoints = remainingGradedPoints - roundedPoints
            })
        })
        setProblems(updatedProblems)
        setGradedPoints(undefined)
    }

    const setAllProblems = (property, value) => {
        var updatedProblems: any[] = cloneDeep(problems)
        updatedProblems.forEach(problem => {
            problem.parts.forEach(part => {
                part[property] = value
            })

        })
        setProblems(updatedProblems)
    }

    const addNewProblems = (guids: Array<string>, index?: number) => {
        setIsLoading(true)
        var newProblems: Array<CPProblem> = []
        window.CPInstructor.getProblemsByGuid(guids, assignmentId, index ?? addProblemIndex).then(response => {
            newProblems = response.data
            var updatedProblems = cloneDeep(problems)
            var spliceIndex = (index ?? addProblemIndex) - 1
            updatedProblems.splice(spliceIndex, 0, ...newProblems)
            setProblems(updatedProblems)
            setIsAddingProblem(false)
            setAddProblemIndex(0)
            var updatedInitialAssignment = cloneDeep(initialCPAssignment)
            window.CPInstructor.getAssignment(assignmentId!, taskPartId)
                .then(response => {
                    setProblems(response.data.problems)
                    updatedInitialAssignment.problems = response.data.problems
                    setProblemLimitEditing(response.problemLimitEditing)
                    setProblemUserIsOwner(response.problemUserIsOwner)
                    setProblemInMultiplePlaces(response.problemInMultiplePlaces)
                    setProblemAnsweredInAssignment(response.problemAnsweredInAssignment)
                    setLimitedEditing(response.limitedEditing)
                    setIsSharedAssignment(response.isSharedAssignment)
                    setInitialCPAssignment(updatedInitialAssignment)
                }).finally(() => {
                    setSelectedProblem(undefined)
                }).catch((error) => {
                    setWarningContent(error.message)
                    // @ts-ignore
                    var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
                    warningModal.show();
                }).finally(() => {
                    setIsLoading(false)
                })
        }).catch((error) => {
            setWarningContent(error.message)
            // @ts-ignore
            var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
            warningModal.show();
        }).finally(() => {
            setIsLoading(false)
        })
    }

    //const selectNewProblem = (guid: string, index?: number, order?: number) => {
    //    setIsLoading(true)
    //    var newProblem: CPProblem = {
    //        name: "",
    //        image: {},
    //        parts: [],
    //        variables: [],
    //        links: [],
    //        topicTaskEntityId: topicTaskEntityId,
    //        taskPartConfigurationId: taskPartConfigurationId,
    //    };
    //    window.CPInstructor.getProblemByGuid(guid, assignmentId, index ?? addProblemIndex).then(response => {
    //        newProblem = response.data
    //        var updatedProblems = cloneDeep(problems)
    //        var spliceIndex = index ?? addProblemIndex
    //        spliceIndex = spliceIndex - 1
    //        updatedProblems.splice(spliceIndex, 0, newProblem)
    //        setProblems(updatedProblems)
    //        setIsAddingProblem(false)
    //        setAddProblemIndex(0)
    //        var updatedInitialAssignment = cloneDeep(initialCPAssignment)
    //        window.CPInstructor.getAssignment(assignmentId!, taskPartId)
    //            .then(response => {
    //                setProblems(response.data.problems)
    //                updatedInitialAssignment.problems = response.data.problems
    //                setProblemLimitEditing(response.problemLimitEditing)
    //                setProblemUserIsOwner(response.problemUserIsOwner)
    //                setProblemInMultiplePlaces(response.problemInMultiplePlaces)
    //                setProblemAnsweredInAssignment(response.problemAnsweredInAssignment)
    //                setLimitedEditing(response.limitedEditing)
    //                setIsSharedAssignment(response.isSharedAssignment)
    //                setInitialCPAssignment(updatedInitialAssignment)
    //            }).finally(() => {
    //                setSelectedProblem(undefined)
    //            }).catch((error) => {
    //                setWarningContent(error.message)
    //                // @ts-ignore
    //                var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
    //                warningModal.show();
    //            }).finally(() => {
    //                setIsLoading(false)
    //            })
    //    }).catch((error) => {
    //        setWarningContent(error.message)
    //        // @ts-ignore
    //        var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
    //        warningModal.show();
    //    }).finally(() => {
    //        setIsLoading(false)
    //    })
    //}

    const cloneProblem = (problem: CPProblem, index: number) => {
        setIsLoading(true)
        window.CPInstructor.setProblem(problem, true, false, assignmentId)
            .then(response => {
                var updatedProblems = cloneDeep(problems)
                var updatedInitialAssignment = cloneDeep(initialCPAssignment)
                var updatedProblemUserIsOwner = cloneDeep(problemUserIsOwner)
                var updatedProblemLimitEditing = cloneDeep(problemLimitEditing)
                var updatedProblemInMultiplePlaces = cloneDeep(problemInMultiplePlaces)
                var updatedProblemAnsweredInAssignment = cloneDeep(problemAnsweredInAssignment)
                updatedProblems[index] = response.data
                updatedInitialAssignment.problems[index] = response.data
                updatedProblemUserIsOwner[response.data.id!] = response.userIsOwner
                updatedProblemLimitEditing[response.data.id!] = response.limitedEditing
                updatedProblemInMultiplePlaces[response.data.id!] = false
                updatedProblemAnsweredInAssignment[response.data.id!] = false
                setProblemUserIsOwner(updatedProblemUserIsOwner)
                setProblemLimitEditing(updatedProblemLimitEditing)
                setProblemInMultiplePlaces(updatedProblemInMultiplePlaces)
                setProblemAnsweredInAssignment(updatedProblemAnsweredInAssignment)
                setProblems(updatedProblems)
                setInitialCPAssignment(updatedInitialAssignment)
                setPopupContent("Problem " + response.data.name + " was successfully cloned.")
                // @ts-ignore
                var popupModal = new bootstrap.Modal(document.getElementById("popupModal"), { backdrop: true, keyboard: true, focus: true })
                popupModal.show();
            }).catch((error) => {
                setWarningContent(error.message)
                // @ts-ignore
                var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
                warningModal.show();
            }).finally(() => {
                setIsLoading(false)
            })
    }

    const editProblem = (problem: CPProblem) => {
        setSelectedProblem(problem)
    }

    const returnProblemToAssignment = (problem: CPProblem) => {
        setIsLoading(true)
        var updatedInitialAssignment: CPAssignment = cloneDeep(initialCPAssignment)
        window.CPInstructor.getAssignment(assignmentId!, taskPartId)
            .then(response => {
                setProblems(response.data.problems)
                updatedInitialAssignment.problems = response.data.problems
                setProblemLimitEditing(response.problemLimitEditing)
                setProblemUserIsOwner(response.problemUserIsOwner)
                setProblemInMultiplePlaces(response.problemInMultiplePlaces)
                setProblemAnsweredInAssignment(response.problemAnsweredInAssignment)
                setLimitedEditing(response.limitedEditing)
                setIsSharedAssignment(response.isSharedAssignment)
                setInitialCPAssignment(updatedInitialAssignment)
            }).finally(() => {
                setSelectedProblem(undefined)
            }).catch((error) => {
                setWarningContent(error.message)
                // @ts-ignore
                var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
                warningModal.show();
            }).finally(() => {
                setIsLoading(false)
            })
    }

    const toggleShowProblem = problemIndex => {
        var updatedHiddenProblems = cloneDeep(hiddenProblems)
        updatedHiddenProblems[problemIndex] = !updatedHiddenProblems[problemIndex]
        setHiddenProblems(updatedHiddenProblems)
    }

    const setAllShow = value => {
        setShowHelp(value)
        setShowEquations(value)
        setShowTags(value)
        setShowGuid(value)
    }

    const addBlankProblem = (problemIndex) => {
        setIsLoading(true)
        window.CPInstructor.getNewProblem(topicTaskEntityId, taskPartConfigurationId).then(response => {
            var newProblem = response.data
            var updatedProblems = cloneDeep(problems)
            updatedProblems.splice(problemIndex, 0, newProblem)
            updateProblemOrders(updatedProblems)
            setProblems(updatedProblems)
            setSelectedProblem(updatedProblems[problemIndex])
        })
            .catch((error) => {
                setWarningContent(error.message)
                // @ts-ignore
                var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
                warningModal.show();
            }).finally(() => {
                setIsLoading(false)
            })
    }

    const updateProblemOrders = (problemArray) => {
        problemArray.forEach((p, pIndex) => p.order = pIndex + 1)
        return problemArray
    }

    const removeProblemInstance = (problem: CPProblem) => {
        if (assignmentId && problem.instanceId) {
            var updatedGradedPoints = (gradedPoints ?? calculateTotalPoints()) - calculateProblemPoints(problem)
            if (updatedGradedPoints < 0) {
                updatedGradedPoints = 0
            }
            setIsLoading(true)
            window.CPInstructor.removeProblemInstance(assignmentId, problem.instanceId, updatedGradedPoints).then(response => {

                setGradedPoints(updatedGradedPoints)
                var updatedProblems = cloneDeep(problems)
                var removedProblemIndex = updatedProblems.findIndex(p => problem.instanceId == p.instanceId)
                updatedProblems.splice(removedProblemIndex, 1)
                setProblems(updatedProblems)
            }).catch((error) => {
                setWarningContent(error.message)
                // @ts-ignore
                var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
                warningModal.show();
            }).finally(() => {
                setIsLoading(false)
            })
        }
        else {
            var updatedGradedPoints = calculateTotalPoints() - calculateProblemPoints(problem)
            setGradedPoints(updatedGradedPoints)
            var updatedProblems = cloneDeep(problems)
            var removedProblemIndex = updatedProblems.findIndex(p => problem.order == p.order && p.id == undefined)
            updatedProblems.splice(removedProblemIndex, 1)
            setProblems(updatedProblems)
        }
    }

    const removePartInstance = (part: CPPart) => {
        if (assignmentId && part.instanceId) {
            var updatedGradedPoints = (gradedPoints ?? calculateTotalPoints()) - getPartPoints(part)
            if (updatedGradedPoints < 0) {
                updatedGradedPoints = 0
            }
            setIsLoading(true)
            window.CPInstructor.removePartInstance(part.instanceId, updatedGradedPoints).then(response => {

                setGradedPoints(updatedGradedPoints)
                var updatedProblems: Array<CPProblem> = cloneDeep(problems)
                var removedPartProblemIndex = updatedProblems.findIndex(problem => {
                    var matchingPart = false;
                    problem.parts.forEach(p => {
                        if (deepEqual(p, part)) {
                            matchingPart = true;
                        }
                    })
                    return matchingPart;
                })
                var removedPartIndex = updatedProblems[removedPartProblemIndex].parts.findIndex(p => p.instanceId == part.instanceId)
                updatedProblems[removedPartProblemIndex].parts.splice(removedPartIndex, 1)
                setProblems(updatedProblems)
                setSelectedProblem(updatedProblems[removedPartProblemIndex])
            }).catch((error) => {
                setWarningContent(error.message)
                // @ts-ignore
                var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
                warningModal.show();
            }).finally(() => {
                setIsLoading(false)
            })
        }
    }

    const previewAssignment = () => {
        if (!CPReactMethods.isNullOrUndefined(assignmentId)) {
            var previewElement = document.getElementById("assignmentPreview")
            setIsLoading(true)
            if (!CPReactMethods.isNullOrUndefined(previewElement)) {
                try {
                    // @ts-ignore
                    window.CPInstructor.previewAssignment(previewElement, assignmentId!, props.courseGuid).then(response => {
                        setIsAssignmentPreview(true)
                    }).catch((error) => {
                        setIsAssignmentPreview(false)
                        setWarningContent(error.message)
                        // @ts-ignore
                        var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
                        warningModal.show();
                    }).finally(() => {
                        setIsLoading(false)
                    })
                }
                catch (ex) {
                    // @ts-ignore
                    setWarningContent(ex)
                    // @ts-ignore
                    var warningModal = new bootstrap.Modal(document.getElementById("warningModal"), { backdrop: true, keyboard: true, focus: true })
                    warningModal.show();
                    setIsAssignmentPreview(false)
                    setIsLoading(false)
                }
            }
        }
    }

    const checkOverrideTotalPoints = () => {
        if (gradedPoints == null || gradedPoints == undefined) {
            setGradedPoints(calculateTotalPoints())
        }
        else {
            setGradedPoints(undefined)
        }
    }

    const returnFromPreviewAssignment = () => {
        var assignmentPreviewDiv = document.getElementById("assignmentPreview")
        setIsAssignmentPreview(false)
        // For some reason clearing this div makes the preview not initialize the second time
        /*while (assignmentPreviewDiv?.firstChild) {
            assignmentPreviewDiv.removeChild(assignmentPreviewDiv.firstChild)
        }*/
    }

    const assignmentHasSubmittedAnswerWarningText = () => {
        var answeredProblemNames = new Array<string>()
        problems.forEach(problem => {
            if (problemAnsweredInAssignment[problem.id!]) {
                answeredProblemNames.push(problem.name)
            }
        })
        var formattedWarning = "Question"
        if (answeredProblemNames.length > 1) {
            formattedWarning += "s "
        }
        else {
            formattedWarning += " "
        }
        answeredProblemNames.forEach((name, index) => {
            if (index != 0 && index == answeredProblemNames.length - 1) {
                formattedWarning += "and "
            }
            formattedWarning += name
            if (index < answeredProblemNames.length - 1 && answeredProblemNames.length > 2) {
                formattedWarning += ", "
            }
            else {
                formattedWarning += " "
            }
        })
        if (answeredProblemNames.length > 1) {
            formattedWarning += "have submitted answers."
        }
        else {
            formattedWarning += "has a submitted answer."
        }
        formattedWarning += " No changes that can affect correctness or allowed attempts for "
        if (answeredProblemNames.length > 1) {
            formattedWarning += "these questions are allowed."
        }
        else {
            formattedWarning += "this question are allowed."
        }
        return formattedWarning;
    }

    return (
        <>
            {selectedProblem != undefined ?
                <ProblemBuilder
                    problem={selectedProblem}
                    assignmentId={assignmentId}
                    returnProblemToAssignment={returnProblemToAssignment}
                    standAloneProblem={false}
                    userIsOwner={CPReactMethods.isNullOrUndefined(selectedProblem.id) || !problemUserIsOwner.hasOwnProperty(selectedProblem.id!) ? true : problemUserIsOwner[selectedProblem.id!]}
                    limitEditing={CPReactMethods.isNullOrUndefined(selectedProblem.id) || !problemLimitEditing.hasOwnProperty(selectedProblem.id!) ? false : problemLimitEditing[selectedProblem.id!]}
                    answeredInAssignment={CPReactMethods.isNullOrUndefined(selectedProblem.id) || !problemAnsweredInAssignment.hasOwnProperty(selectedProblem.id!) ? false : problemAnsweredInAssignment[selectedProblem.id!]}
                    inMultiplePlaces={CPReactMethods.isNullOrUndefined(selectedProblem.id) || !problemInMultiplePlaces.hasOwnProperty(selectedProblem.id!) ? false : problemInMultiplePlaces[selectedProblem.id!]}
                    assignmentLimitedEditing={limitedEditing}
                    assignmentIsShared={isSharedAssignment}
                    removePartInstance={removePartInstance}
                />
                : <>
                    {isAddingProblem == true ? <ProblemSearchComponent
                        addNewProblems={addNewProblems}
                        setIsAddingProblem={setIsAddingProblem}
                        isProblemLibrary={false}
                    /> :
                        isAssignmentPreview ?
                            <div className="container-fluid px-3">
                                <BreadcrumbComponent
                                    breadcrumbs={[{ text: "Assignment Builder", function: returnFromPreviewAssignment }, { text: "Assignment Preview" }]}
                                />
                                <button type="button" className="btn btn-primary mx-2 mb-2" onClick={returnFromPreviewAssignment}>Back to Assignment Builder</button>
                            </div>
                            :
                            <div className={isLoading ? "container-fluid px-3 busy" : "container-fluid px-3"}>
                                <div className="row">
                                    <div className="col">
                                        <BreadcrumbComponent
                                            breadcrumbs={[{ text: "Assignment Builder" }]}
                                        />
                                        {isSharedAssignment ?
                                            <div className="alert alert-warning alert-dismissible fade show" role="alert">
                                                <b>This assignment is linked to a task in another course. Any changes to this assignment or its problems will affect other instances of this assignment. Save any pending changes then clone this assignment if you only want to change this instance of the assignment.</b>
                                                <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                                            </div> : ""
                                        }
                                        {limitedEditing ?
                                            <div className="alert alert-warning alert-dismissible fade show" role="alert">
                                                <b>{assignmentHasSubmittedAnswerWarningText()}</b>
                                                <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                                            </div> : ""
                                        }
                                        <button type="button" className={pendingChanges ? "btn btn-primary mx-2 mb-2" : "btn btn-outline-primary mx-2 mb-2"} onClick={() => saveAssignmentToApi(false, false)}>Save Assignment</button>
                                        <button type="button" disabled={pendingChanges} className="btn btn-primary mx-2 mb-2" onClick={previewAssignment}>Preview Assignment</button>
                                        {isSharedAssignment ? <button type="button" disabled={pendingChanges} className="btn btn-primary mx-2 mb-2" data-bs-toggle="modal" data-bs-target="#cloneConfirmationModal">Clone Assignment</button> : ""}
                                        {lastTimeSaved ? <label className="mx-2 mb-2">Assignment Last Saved at {lastTimeSaved}</label> : ""}
                                    </div>
                                </div>

                                <div className="card my-3">
                                    <div className="card-header">
                                        <b>Tools</b>
                                    </div>
                                    <div className="row align-items-center">
                                        <div className="col">
                                            <div className="card-body">
                                                <div className="row ">
                                                    <div className="col-auto">
                                                        <strong>Show:</strong> <button role="button" type="button" className="btn btn-sm btn-secondary" onClick={() => setAllShow(true)}>All</button> <button role="button" type="button" className="btn btn-sm btn-secondary" onClick={() => setAllShow(false)}>None</button>
                                                    </div>
                                                    <div className="col-auto">
                                                        <input role="button" className="form-check-input" type="checkbox" checked={showHelp} onChange={e => setShowHelp(e.currentTarget.checked)} id="showHelp" />
                                                        <label className="form-check-label px-1" htmlFor="showHelp">Help/Hints</label>
                                                    </div>
                                                    <div className="col-auto">
                                                        <input role="button" className="form-check-input" type="checkbox" checked={showEquations} onChange={e => setShowEquations(e.currentTarget.checked)} id="showEquations" />
                                                        <label className="form-check-label px-1" htmlFor="showEquations">Equations</label>
                                                    </div>
                                                    <div className="col-auto">
                                                        <input role="button" className="form-check-input" type="checkbox" checked={showTags} onChange={e => setShowTags(e.currentTarget.checked)} id="showTags" />
                                                        <label className="form-check-label px-1" htmlFor="showTags">Tags</label>
                                                    </div>
                                                    <div className="col-auto">
                                                        <input role="button" className="form-check-input" type="checkbox" checked={showGuid} onChange={e => setShowGuid(e.currentTarget.checked)} id="showId" />
                                                        <label className="form-check-label px-1" htmlFor="showId">Id</label>
                                                    </div>
                                                </div>
                                                <div className="row">
                                                    <div className="col-auto">
                                                        <strong>Jump to Problem:</strong>
                                                    </div>
                                                    <div className="col-auto">
                                                        {problems.map((problem, problemIndex) => {
                                                            return (
                                                                <>
                                                                    <a href={"#problem" + (problemIndex + 1).toString()}>{problemIndex + 1}</a>&emsp;
                                                                </>
                                                            )
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card my-3">
                                    <div className="card-header">
                                        <b>Assignment Name</b>
                                    </div>
                                    <div className="row align-items-center ">
                                        <div className="col">
                                            <div className="card-body">
                                                <input className="form-control" type="text" maxLength={75} onChange={e => changeName(e.currentTarget.value)} value={name} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card my-3">
                                    <div className="card-header">
                                        <b>Assignment Id</b>
                                    </div>
                                    <div className="row align-items-center ">
                                        <div className="col">
                                            <div className="card-body">
                                                {assignmentGuid}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card my-3">
                                    <div className="card-header">
                                        <b>Assignment Overview</b>
                                    </div>
                                    <div className="row align-items-center ">
                                        <div className="col">
                                            <div className="card-body">
                                                <RichTextEditorComponent
                                                    editorState={editorState}
                                                    changeEditor={changeEditor}
                                                    placeholder="Assignment Overview"
                                                    maxCharacters={750}
                                                    showSymbolDropdown={true}
                                                    readOnly={false}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="card my-3">
                                    <div className="card-header" role="button" onClick={() => setShowAssignmentBuilderConfig(!showAssignmentBuilderConfig)}>
                                        <span className="fw-bold fs-4">{showAssignmentBuilderConfig ? <FontAwesomeIcon icon={faMinus} /> : <FontAwesomeIcon icon={faPlus} />}</span>
                                        <b>&emsp; Assignment Config</b>
                                    </div>
                                    <Collapse in={showAssignmentBuilderConfig}>
                                        <div className="row">
                                            <div className="col">
                                                <div className="card-body">
                                                    <div className="row" >
                                                        <div className="col-md-6">
                                                            <div className="row configrow">
                                                                <div className="col configlabel">
                                                                    Default Error Allowance
                                                                </div>
                                                                <div className="col ">
                                                                    <div className="input-group">
                                                                        <input disabled={limitedEditing} type="number" min="0" className="form-control text-center col configInput" onChange={e => changeDefaultPlusMinus(e.currentTarget)} value={defaultPlusMinus} />
                                                                        <select disabled={limitedEditing} className="form-select col configDropdown" onChange={e => changeDefaultPlusMinusIsPercent(e.currentTarget)} value={defaultPlusMinusIsPercent.toString()}>
                                                                            <option value={"true"}>%</option>
                                                                            <option value={"false"}>Units</option>
                                                                        </select>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            {!isGlobalAdmin() ?
                                                                <>
                                                                    <div className="row configrow">
                                                                        <div className="col configlabel">
                                                                            Default Max Attempts
                                                                        </div>
                                                                        <div className="col ">
                                                                            <input disabled={limitedEditing} type="text" min="0" className="form-control text-center configInput" onChange={e => changeDefaultMaxAttempts(e.currentTarget)} value={defaultMaxAttempts.toString()} />
                                                                        </div>
                                                                    </div>
                                                                    <div className="row configrow">
                                                                        <div className="col configlabel">
                                                                            Default Unpenalized Attempts
                                                                        </div>
                                                                        <div className="col ">
                                                                            <input disabled={limitedEditing} type="text" min="1" className="form-control text-center configInput" onChange={e => changeDefaultUnpenalizedAttempts(e.currentTarget)} value={defaultUnpenalizedAttempts.toString()} />
                                                                        </div>
                                                                    </div>
                                                                    <div className="row configrow">
                                                                        <div className="col configlabel">
                                                                            Default Penalty %
                                                                        </div>
                                                                        <div className="col ">
                                                                            <input disabled={limitedEditing} type="text" min="0" className="form-control text-center configInput" step="5" onChange={e => changeDefaultPenalty(e.currentTarget)} value={defaultPenalty} />
                                                                        </div>
                                                                    </div>
                                                                </>
                                                                : ""}
                                                        </div>
                                                        <div className="col-md-6">
                                                            {!isGlobalAdmin() ?
                                                                <>

                                                                    <div className="row configrow">
                                                                        <div className="col configlabel">
                                                                            Default Point Value
                                                                        </div>
                                                                        <div className="col">
                                                                            <div className="row">
                                                                                <div className="col ">
                                                                                    <input type="text" min="0" className="form-control text-center configInput" onChange={e => changeDefaultPointValue(e.currentTarget)} value={defaultPointValue} />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="row configrow">
                                                                        <div className="col configlabel">
                                                                            Total Possible Points
                                                                        </div>
                                                                        <div className="col">
                                                                            <div className="row">
                                                                                <div className="col">
                                                                                    <input type="text" readOnly className="form-control text-center configInput" onChange={() => { }} value={calculateTotalPoints()} />
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    {gradedPoints != undefined ?
                                                                        <>
                                                                            <div className="row configrow">
                                                                                <div className="col configlabel">
                                                                                    Assignment Point Value
                                                                                </div>
                                                                                <div className="col ">
                                                                                    <input type="text" className="form-control text-center configInput" onChange={e => changeGradedPoints(e.currentTarget)} value={gradedPoints} />
                                                                                </div>
                                                                            </div>
                                                                            <div className="row configrow">
                                                                                <div className="col configlabel">
                                                                                </div>
                                                                                <div className="col text-center">
                                                                                    <button type="button" className="btn btn-secondary" data-bs-toggle="modal" data-bs-target="#recalculatePointsModal">Recalculate Points</button>
                                                                                </div>
                                                                            </div>
                                                                        </> : ""}
                                                                    <div className="row configrow">
                                                                        <div className="col configlabel">
                                                                            Override Total Score
                                                                        </div>
                                                                        <div className="col text-center">
                                                                            <input type="checkbox" checked={gradedPoints != undefined} className="form-check-input" onChange={checkOverrideTotalPoints} />
                                                                        </div>
                                                                    </div>
                                                                </>
                                                                : ""}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Collapse>
                                </div>
                                <div className="card my-3">
                                    <div className="card-header" role="button" onClick={() => setShowAssignmentBuilderHelp(!showAssignmentBuilderHelp)} >
                                        <span className="fw-bold fs-4">{showAssignmentBuilderHelp ? <FontAwesomeIcon icon={faMinus} /> : <FontAwesomeIcon icon={faPlus} />}</span>
                                        <b>&emsp;Assignment Builder Help</b>
                                    </div>
                                    <Collapse in={showAssignmentBuilderHelp}>
                                        <div className="row align-items-center">
                                            <div className="col">
                                                <div className="card-body" dangerouslySetInnerHTML={{ __html: props.helpTextHtml }}></div>
                                            </div>
                                        </div>
                                    </Collapse>
                                </div>
                                {problems.length == 0 ?
                                    <div className="card my-3">
                                        <div className="card-header">
                                            <div className="col-auto">
                                                <b><button type="button" className="btn btn-primary" data-bs-toggle="modal" data-bs-target="#addBlankOrPremadeProblemModal">Add Problem</button></b>
                                            </div>
                                        </div>

                                    </div>
                                    :
                                    problems.map((problem, problemIndex) => {
                                        return (
                                            <AssignmentBuilderProblemCard
                                                key={problem.instanceId}
                                                problem={problem}
                                                problemIndex={problemIndex}
                                                problemsLength={problems.length}
                                                showGuid={showGuid}
                                                showEquations={showEquations}
                                                showHelp={showHelp}
                                                showTags={showTags}
                                                answeredInAssignment={problemAnsweredInAssignment[problem.id!]}
                                                problemUserIsOwner={problemUserIsOwner[problem.id!]}
                                                problemInMultiplePlaces={problemInMultiplePlaces[problem.id!]}
                                                defaultPointValue={defaultPointValue}
                                                defaultMaxAttempts={defaultMaxAttempts}
                                                defaultUnpenalizedAttempts={defaultUnpenalizedAttempts}
                                                defaultPenalty={defaultPenalty}
                                                defaultPlusMinus={defaultPlusMinus}
                                                defaultPlusMinusIsPercent={defaultPlusMinusIsPercent}
                                                moveProblemUp={moveProblemUp}
                                                moveProblemDown={moveProblemDown}
                                                editProblem={editProblem}
                                                isGlobalAdmin={isGlobalAdmin}
                                                calculateProblemPoints={calculateProblemPoints}
                                                changePointValue={changePointValue}
                                                changeMaxAttempts={changeMaxAttempts}
                                                changeUnpenalizedAttempts={changeUnpenalizedAttempts}
                                                changePenalty={changePenalty}
                                                changePlusMinus={changePlusMinus}
                                                changePlusMinusIsPercent={changePlusMinusIsPercent}
                                                removeProblemInstance={removeProblemInstance}
                                                addBlankProblem={addBlankProblem}
                                                setAddProblemIndex={setAddProblemIndex}
                                                setIsAddingProblem={setIsAddingProblem}
                                                addNewProblems={addNewProblems}
                                                cloneProblem={cloneProblem} />
                                        )
                                    })}
                                <ConfirmationModalComponent
                                    itemId="recalculatePointsModal"
                                    itemContent={"Do you want to scale all problem's points to make Total Points equal to Assignment Point Value of  " + gradedPoints + "?"}
                                    accept={adjustPointTotal}
                                />
                                <ConfirmationModalComponent
                                    itemId="cloneConfirmationModal"
                                    itemContent="Do you want to clone this assignment and unlink it from other tasks?"
                                    accept={() => saveAssignmentToApi(false, true)}
                                />
                            </div>
                    }
                </>}
            <WarningModalComponent
                itemId="warningModal"
                itemContent={warningContent}
            />
            <PopupModalComponent
                itemId="popupModal"
                itemContent={popupContent}
            />
            {
                problems.length == 0 ?
                    <TwoOptionModalComponent
                        itemId="addBlankOrPremadeProblemModal"
                        itemContent="Do you want to add a blank problem or select an existing one?"
                        button1Text="Create Blank Problem"
                        button1Function={() => addBlankProblem(0)}
                        button2Text="Select Existing Problem"
                        button2Function={() => { setAddProblemIndex(1); setIsAddingProblem(true); }}
                    /> : ""
            }
            <div className="container-fluid px-3" hidden={!isAssignmentPreview} id="assignmentPreview" />
        </>
    );
};



export default AssignmentBuilder;